import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime, money } from "@/lib/format";
import { OUTCOME_LABELS, OUTCOME_STYLES } from "./dashboardTokens";

/** How far a session got before it stopped. */
const progressNote = (row) => {
  if (row.completed_contact_gate) return "Completed the gate";
  if (row.reached_contact_gate) return "Reached the gate, didn't finish";
  return "Didn't reach the gate";
};

/** Which step they were on, and whether they finished. */
const ProgressCell = ({ row }) => (
  <TableCell className="whitespace-nowrap text-xs">
    {row.total_steps
      ? `Step ${Math.min(row.step_index + 1, row.total_steps)} of ${row.total_steps}`
      : "\u2014"}
    <span className="mt-0.5 block text-muted-foreground">{progressNote(row)}</span>
  </TableCell>
);

/** Whatever contact details the session managed to capture, if any. */
const ContactCell = ({ row }) => {
  if (row.full_name) {
    return (
      <TableCell className="text-sm">
        <span className="block font-medium">{row.full_name}</span>
        <span className="block text-xs text-muted-foreground">{row.company}</span>
        <span className="block text-xs text-muted-foreground">
          {row.email} · {row.phone}
        </span>
      </TableCell>
    );
  }
  if (row.resume_email) {
    return (
      <TableCell className="text-sm">
        <span className="text-xs text-muted-foreground">
          {row.resume_email}
          <span className="block">(resume link only)</span>
        </span>
      </TableCell>
    );
  }
  return (
    <TableCell className="text-sm">
      <span className="text-xs text-muted-foreground">No details captured</span>
    </TableCell>
  );
};

/** Every calculator session, finished or not. */
export const SessionsTable = ({ rows, onOpenSession }) => (
  <Table data-testid="admin-sessions-table">
    <TableHeader>
      <TableRow>
        <TableHead>Started</TableHead>
        <TableHead>Service</TableHead>
        <TableHead>Progress</TableHead>
        <TableHead>Outcome</TableHead>
        <TableHead>Contact</TableHead>
        <TableHead className="text-right">Quote</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.map((row) => (
        <TableRow
          key={row.session_id}
          data-testid="admin-session-row"
          onClick={() => onOpenSession(row.session_id)}
          className="cursor-pointer"
        >
          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
            {formatDateTime(row.created_at)}
          </TableCell>
          <TableCell className="text-sm">
            {row.service_label || <span className="text-muted-foreground">Not selected</span>}
          </TableCell>
          <ProgressCell row={row} />
          <TableCell>
            <Badge
              variant="outline"
              className={`font-normal ${OUTCOME_STYLES[row.outcome] || OUTCOME_STYLES.in_progress}`}
            >
              {OUTCOME_LABELS[row.outcome] || row.outcome}
            </Badge>
            {row.outcome === "fallback" && row.fallback_reasons?.length ? (
              <span className="mt-1 block max-w-[220px] truncate text-[11px] text-muted-foreground">
                {row.fallback_reasons.map((r) => r.code).join(", ")}
              </span>
            ) : null}
          </TableCell>
          <ContactCell row={row} />
          <TableCell className="text-right">
            {row.quote_ref ? (
              <>
                <span className="block font-amount text-sm font-bold">
                  {money(row.quote_total)}
                </span>
                <span className="block font-amount text-[11px] text-muted-foreground">
                  {row.quote_ref}
                </span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
